import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { TutorBillingChangeType } from './enums'
import type { TutorStatus } from '@shared/config'

import { useListsStore } from '@shared/model'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingQuery } from './tutor-billing-query.hook'
import { TUTOR_BILLING_CHANGE_TYPE_OPTIONS, TUTOR_BILLING_TUTOR_STATUS_ORDER } from '../config/tutor-billing'
import { filtersToQuery } from '../lib/tutor-billing-query'

interface FiltersTutorBillingReturn {
  search: WritableComputedRef<string>
  paymentServices: WritableComputedRef<string[] | undefined>
  changeType: WritableComputedRef<TutorBillingChangeType | undefined>
  paymentServiceOptions: ComputedRef<AppSelectOption<string>[]>
  tutorStatuses: WritableComputedRef<TutorStatus[] | undefined>
  tutorStatusOptions: ComputedRef<AppSelectOption<TutorStatus>[]>
  changeTypeOptions: AppSelectOption<TutorBillingChangeType>[]
  isLoading: ComputedRef<boolean>
  hasSelection: ComputedRef<boolean>
  setSearch: () => Promise<void>
  setClear: () => Promise<void>
  setOpenBulkEdit: () => void
  setOpenBulkConfirm: () => void
  setOpenBulkErase: () => void
}

export const useFiltersTutorBilling = (): FiltersTutorBillingReturn => {
  const store = useTutorBillingStore()
  const listsStore = useListsStore()
  const { setQuery } = useTutorBillingQuery()

  const search = computed<string>({
    get: () => store.getFilters.search ?? '',
    set: (value) => store.setFilterSearch(value || null),
  })

  const paymentServices = computed<string[] | undefined>({
    get: () => store.getFilters.paymentServices,
    set: (value) => store.setFilterPaymentServices(value ?? []),
  })

  const changeType = computed<TutorBillingChangeType | undefined>({
    get: () => store.getFilters.changeType ?? undefined,
    set: (value) => store.setFilterChangeType(value ?? null),
  })

  const paymentServiceOptions = computed<AppSelectOption<string>[]>(() =>
    listsStore.getTutorBillingPaymentServiceOptions.map(option => ({ ...option, value: String(option.value) })),
  )

  const tutorStatuses = computed<TutorStatus[] | undefined>({
    get: () => store.getFilters.tutorStatuses,
    set: (value) => store.setFilterTutorStatuses(value ?? []),
  })

  const tutorStatusOptions = computed<AppSelectOption<TutorStatus>[]>(() =>
    TUTOR_BILLING_TUTOR_STATUS_ORDER.flatMap((status) => {
      const option = listsStore.getTutorStatusOptions.find(item => item.value === status)
      return option ? [{ value: status, text: option.text }] : []
    }),
  )

  const isLoading = computed<boolean>(() => store.isLoading)
  const hasSelection = computed<boolean>(() => store.hasSelection)

  const setSearch = async (): Promise<void> => {
    if (store.isLoading) return
    store.setFilterSearch(store.getFilters.search?.trim() || null)
    store.setCurrentPage(1)
    store.clearSelection()
    await setQuery({
      ...filtersToQuery(store.getFilters),
      per_page: String(store.getPerPage),
    })
  }

  const setClear = async (): Promise<void> => {
    if (store.isLoading) return
    store.resetFilters()
    store.clearSelection()
    await setQuery({})
  }

  const setOpenBulkEdit = (): void => {
    store.openModal(TutorBillingModal.BulkFlags)
  }

  const setOpenBulkConfirm = (): void => {
    store.openModal(TutorBillingModal.BulkConfirm)
  }

  const setOpenBulkErase = (): void => {
    store.openModal(TutorBillingModal.BulkErase)
  }

  return {
    search,
    paymentServices,
    changeType,
    paymentServiceOptions,
    tutorStatuses,
    tutorStatusOptions,
    changeTypeOptions: TUTOR_BILLING_CHANGE_TYPE_OPTIONS,
    isLoading,
    hasSelection,
    setSearch,
    setClear,
    setOpenBulkEdit,
    setOpenBulkConfirm,
    setOpenBulkErase,
  }
}
